import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useRef } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "@radix-ui/react-icons";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";
import { getDatesWithDayNumber, getNextYearDate } from "@/utils";

import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Calendar } from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion/accordion";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { HolidayListTable } from "./holiday-list-table/holiday-list-table";

import { createHolidayList } from "@/config/mutation/admin";
import { AxiosError } from "axios";
import { Separator } from "@/components/ui/separator/separator";
import { HalfDaysTable } from "./half-days-table/half-days-table";

const formSchema = z.object({
  name: z.string().min(1, {
    message: "Holiday list name required",
  }),
  fromDate: z.date().optional(),
  toDate: z.date().optional(),
  totalHolidays: z.number().optional(),
  weeklyHoliday: z.number().optional(),
  holidays: z.array(
    z.object({
      holidayName: z.string(),
      date: z.date(),
      description: z.string(),
    })
  ),
  halfDays: z
    .array(
      z.object({
        dayOfWeek: z.number(),
        minimumHours: z.number(),
      })
    )
    .optional(),
});

export type FormValues = z.infer<typeof formSchema>;

export const NewHolidayListForm = () => {
  const isFirstTime = useRef(true);
  const adminKey = Cookies.get("key") as string;

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      fromDate: undefined,
      toDate: undefined,
      totalHolidays: 0,
      holidays: [],
      halfDays: [],
      weeklyHoliday: undefined,
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createHolidayList(adminKey),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // console.log(values);

    mutate(
      {
        name: values.name,
        holidays: values.holidays,
        halfDays: values.halfDays,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["holidays"],
          });

          toast.success("New Holiday List Created!");

          navigate("/admin/holidays", {
            replace: true,
          });
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING HOLIDAY LIST", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  // const fromDate = useWatch({
  //   control: form.control,
  //   name: "fromDate",
  // });

  // console.log("fromDate", fromDate);

  const handleGenerateHolidays = () => {
    if (
      form.getValues("fromDate") === undefined ||
      form.getValues("toDate") === undefined ||
      form.getValues("weeklyHoliday") === undefined
    ) {
      console.log("In Check");
      console.log(form.getValues("weeklyHoliday"));
      return;
    }

    const result = getDatesWithDayNumber(
      form.getValues("fromDate") as Date,
      form.getValues("toDate") as Date,
      form.getValues("weeklyHoliday") as number
    );

    const formattedValue = result.map((hol) => ({
      holidayName: hol.name,
      date: hol.date,
      description: hol.name,
    }));

    const prevValues = form.getValues("holidays");

    const all = [...formattedValue, ...prevValues].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );

    form.setValue("holidays", all);
  };

  return (
    <div className="h-full rounded-md relative">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex items-center justify-between sticky top-0 px-4 z-30 rounded-md bg-background">
            <h3 className="text-2xl font-medium">New Holiday List</h3>

            <Button type="submit" size="sm" disabled={isPending}>
              Save
            </Button>
          </div>

          <div className="py-5 px-5 mt-5 rounded-b-md">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Holiday List Name</FormLabel>
                    <FormControl>
                      <Input disabled={isPending} type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator className="my-5 bg-gray-500" />

            <div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="fromDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-0 gap-3 mt-1">
                      <FormLabel>From Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              disabled={isPending}
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(day) => {
                              field.onChange(day);
                              if (isFirstTime.current) {
                                const nextYearDate = getNextYearDate(
                                  day as Date
                                );
                                form.setValue("toDate", nextYearDate);
                                isFirstTime.current = false;
                              }
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="toDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-0 gap-3 mt-1">
                      <FormLabel>To Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              disabled={isPending}
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(day) => {
                              field.onChange(day);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {form.getValues("fromDate") && form.getValues("toDate") && (
                <div className="border-t mt-4">
                  <Accordion type="single" collapsible disabled={isPending}>
                    <AccordionItem value="item-1" className="">
                      <AccordionTrigger>Add Weekly Holidays</AccordionTrigger>
                      <AccordionContent>
                        <div className="px-3">
                          <div className="grid grid-cols-1 lg:grid-cols-2">
                            <FormField
                              control={form.control}
                              name="weeklyHoliday"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Weekly Off</FormLabel>
                                  <Select
                                    value={String(field.value)}
                                    onValueChange={(value) => {
                                      field.onChange(Number(value));
                                    }}
                                  >
                                    <SelectTrigger className="">
                                      <SelectValue placeholder="Weekly Day" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="0">Sunday</SelectItem>
                                      <SelectItem value="1">Monday</SelectItem>
                                      <SelectItem value="2">Tuesday</SelectItem>
                                      <SelectItem value="3">
                                        Wednesday
                                      </SelectItem>
                                      <SelectItem value="4">
                                        Thursday
                                      </SelectItem>
                                      <SelectItem value="5">Friday</SelectItem>
                                      <SelectItem value="6">
                                        Saturday
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="flex items-center mt-3">
                            <Button
                              size="sm"
                              type="button"
                              onClick={handleGenerateHolidays}
                            >
                              Add to Holidays
                            </Button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}

              <HolidayListTable />
            </div>

            <Separator className="my-5 bg-gray-500" />

            <div>
              <HalfDaysTable />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};
